import { FilterChangedEvent } from '@ag-grid-community/core/dist/esm/es6/events';
import { IFloatingFilter, IFloatingFilterParams } from '@ag-grid-community/core/dist/esm/es6/filter/floating/floatingFilter';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { AmpPlugin } from '@easepick/amp-plugin';
import { Core, easepick } from '@easepick/core';
import { KbdPlugin } from '@easepick/kbd-plugin';
import { DATEPICKER_STYLES } from 'shared/constants/general-constants';

@Component({
    selector: 'app-date-floating-filter',
    templateUrl: './date-floating-filter.component.html',
    styleUrls: ['./date-floating-filter.component.scss'],
    standalone: false,
})
export class DateFloatingFilterComponent implements IFloatingFilter, AfterViewInit, OnDestroy {
    @ViewChild('dateInput', { read: ElementRef }) dateInput!: ElementRef;

    public dateInputControl = new FormControl<string | null>(null);
    public format: string = 'YYYY-MM-DD';
    public hostElement!: HTMLElement;

    private params!: IFloatingFilterParams;
    private datepicker!: Core;

    constructor(
        private zone: NgZone,
        private cdr: ChangeDetectorRef,
    ) {}

    public agInit(params: IFloatingFilterParams): void {
        this.params = params;
    }

    public ngAfterViewInit(): void {
        this.format = this.params.column.getColDef().filterParams.format || this.format;
        this.hostElement = document.createElement('div');
        this.hostElement.classList.add(`calendar-picker-${(this.params as any).column.colId}-host`);
        document.body.querySelector('.ag-root-wrapper')?.appendChild(this.hostElement);
        this.hostElement.style.fontSize = '0';

        this.zone.runOutsideAngular(() => {
            this.datepicker = new easepick.create({
                element: document.body.querySelector(`.calendar-picker-${(this.params as any).column.colId}-host`),
                readonly: false,
                firstDay: 1,
                format: this.format,
                plugins: [AmpPlugin, KbdPlugin],
                css: ['https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.1/dist/index.css'],
                AmpPlugin: {
                    darkMode: false,
                    dropdown: {
                        months: true,
                        years: true,
                        maxYear: 2100,
                    },
                },
            } as any);
        });

        this.datepicker.on('select', (event: any) =>
            this.zone.run(
                () => {
                    const formattedDate = event.detail.date.format(this.format);
                    const gridFilterModel = this.params.api.getFilterModel();

                    this.dateInputControl.setValue(formattedDate);
                    gridFilterModel[this.params.column.getColId()] = {
                        filterType: 'text',
                        type: 'equals',
                        filter: formattedDate,
                    };

                    this.params.api.setFilterModel(gridFilterModel);
                },
                this,
                [event],
            ),
        );

        this.datepicker.ui.wrapper.classList.add(`calendar-picker-${(this.params as any).column.colId}-parasite`);

        this.dateInput.nativeElement.addEventListener(
            'keydown',
            (event: any) => {
                if (event.key === 'Escape' && this.datepicker.isShown()) {
                    event.preventDefault();
                    this.closeCalendar(event);
                }
            },
            { capture: true },
        );

        const easePickStyle = document.createElement('style');
        easePickStyle.innerHTML = DATEPICKER_STYLES;
        this.datepicker.ui.shadowRoot.append(easePickStyle);
    }

    public closeCalendar(event: Event) {
        this.zone.runOutsideAngular(() => {
            if (this.datepicker.isShown()) {
                this.datepicker.hide();
                event.stopPropagation();
            }
        });
    }

    public openCalendar(event: Event) {
        event.preventDefault();
        event.stopPropagation();
        this.positionCalendarBelowInput();
        this.datepicker.show();
    }

    private positionCalendarBelowInput() {
        // 1. Directly select the common parent using the provided class
        const commonParentClass = 'ag-root-wrapper'; // Replace with the actual class name
        const commonParent = document.querySelector(`.${commonParentClass}`);

        // 2. Get the offset of the input relative to the common parent
        const { top, left } = getOffsetRelativeToParent(this.dateInput.nativeElement, commonParent);

        // 3. Position the calendar div
        this.hostElement.style.position = 'absolute';
        this.hostElement.style.left = `${left}px`;
        this.hostElement.style.top = `${top + this.dateInput.nativeElement.offsetHeight}px`; // Add the height of the input to position the div below it
    }

    public clearDate(): void {
        this.datepicker.clear();
        this.datepicker.gotoDate(new Date());
        this.dateInputControl.setValue(null);

        const gridFilterModel = this.params.api.getFilterModel();
        delete gridFilterModel[this.params.column.getColId()];

        this.params.api.setFilterModel(gridFilterModel);
    }

    public ngOnDestroy() {
        this.zone.runOutsideAngular(() => {
            this.datepicker.destroy();
        });
    }

    public onParentModelChanged(parentModel: any, filterChangedEvent?: FilterChangedEvent | null): void {
        this.dateInputControl.setValue(parentModel?.filter);
        if (parentModel?.filter) {
            this.datepicker.gotoDate(parentModel.filter);
            this.datepicker.setDate(parentModel.filter);
        }
        this.cdr.detectChanges();
    }
}

function getOffsetRelativeToParent(element: any, parent: any) {
    let top = 0;
    let left = 0;
    let currentElement = element;

    while (currentElement && currentElement !== parent) {
        top += currentElement.offsetTop;
        left += currentElement.offsetLeft;
        currentElement = currentElement.offsetParent;
    }

    return { top, left };
}
